import { inject, provide, ref, type Component, type InjectionKey, type Ref } from 'vue';

// An ordinary clickable row.
export type ContextMenuActionItem = {
  kind: 'action';
  label: string;
  icon?: Component;
  action: () => void;
  // Renders the row in the app's danger/red color (text + icon) — set on destructive entries like
  // Delete so they read visually distinct from ordinary actions, same intent as bulma's own
  // `is-danger` button modifier elsewhere in the app.
  danger?: boolean;
};

// A row that opens a nested panel instead of doing something itself (today: "Verktyg ›").
//
// `items` is typed to the ACTION member specifically, so a second nesting level is a compile error
// rather than a runtime surprise. Nothing needs one, deep cascades are miserable to hit with a mouse,
// and widening it later is one word. It also lets the panel keep `overflow: hidden` — there can never
// be a grandchild panel for it to clip.
export type ContextMenuSubmenuItem = {
  kind: 'submenu';
  label: string;
  icon?: Component;
  items: ContextMenuActionItem[];
};

// Discriminated on `kind`, mirroring ContextMenuSection below rather than adding an optional
// `children` field. An optional field would make `action` optional for EVERY item and permit an item
// carrying both or neither — and it would leave this file with two different extension conventions
// one type apart.
export type ContextMenuItem = ContextMenuActionItem | ContextMenuSubmenuItem;

// A header names/icons the thing a menu was opened for — non-interactive, always first when present.
// Single-file/single-folder/synthetic menus have one; multi-selection menus have no canonical single
// item to show, and the grid's background menu names the folder the actions will land in.
export type ContextMenuHeaderSection = {
  kind: 'header';
  icon: Component;
  color: string;
  name: string;
};

// A group is a logical cluster of clickable items — the menu renders a divider between adjacent
// sections automatically, so builders no longer place 'divider' literals themselves or track
// "only divide if the neighboring group is non-empty": an absent group is just an absent section.
export type ContextMenuGroupSection = {
  kind: 'group';
  items: ContextMenuItem[];
};

export type ContextMenuSection = ContextMenuHeaderSection | ContextMenuGroupSection;

export type FileManagerContextMenuState = {
  isOpen: Ref<boolean>;
  position: Ref<{ x: number; y: number }>;
  items: Ref<ContextMenuSection[]>;
  open: (position: { x: number; y: number }, items: ContextMenuSection[]) => void;
  close: () => void;
};

export const fileManagerContextMenuKey: InjectionKey<FileManagerContextMenuState> =
  Symbol('fileManagerContextMenu');

// Generic shared context-menu state: only one menu is ever open at a time by construction — a
// future item-level menu (rename/delete) calls the same `open()` with a different `items` array,
// which just replaces whatever was open. Not a background-menu-specific composable.
export const provideFileManagerContextMenu = () => {
  const isOpen = ref(false);
  const position = ref({ x: 0, y: 0 });
  const items = ref<ContextMenuSection[]>([]);

  const open = (nextPosition: { x: number; y: number }, nextItems: ContextMenuSection[]) => {
    position.value = nextPosition;
    items.value = nextItems;
    isOpen.value = true;
  };

  const close = () => {
    isOpen.value = false;
  };

  const state: FileManagerContextMenuState = {
    isOpen,
    position,
    items,
    open,
    close,
  };
  provide(fileManagerContextMenuKey, state);
  return state;
};

export const useFileManagerContextMenu = () => {
  const state = inject(fileManagerContextMenuKey);
  if (!state) {
    throw new Error(
      'useFileManagerContextMenu() must be called within a component tree provided by provideFileManagerContextMenu()',
    );
  }
  return state;
};

// Shared by every "build a menu, then open it if there's anything to show" call site (grid item,
// tree item, both background menus, DetailsView's "⋮") — previously each repeated the same
// build → bail-if-empty → open sequence by hand. Returns whether it opened so a native-event caller
// can decide whether to call its own event.preventDefault() (a click-triggered caller like
// DetailsView's "⋮" button has no such event to prevent).
export const openContextMenuIfAny = (
  position: { x: number; y: number },
  items: ContextMenuSection[],
  menu: Pick<FileManagerContextMenuState, 'open'>,
): boolean => {
  // A header alone IS something to show, and this used to say the opposite. That was correct only
  // while every single-item menu carried a "Kopiera namn" entry, which the old comment here said out
  // loud. Copying the name is now what clicking the header does, so a header with no groups behind it
  // is a menu with exactly one action rather than an empty one — and suppressing it would silently
  // remove copy-name from precisely the tiers that have nothing else (read-only, no webUrl).
  if (items.length === 0) return false;
  menu.open(position, items);
  return true;
};
